/**
 * VolumeDetailsModal Component
 *
 * Modal for viewing volume details.
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { useVolumeDetails } from '../hooks/useVolumeDetails';
import { formatDate, formatBytes } from '../../../lib/utils/format';
import { getErrorMessage } from '../../../lib/utils/errors';

interface VolumeDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  volumeId: string;
}

/**
 * VolumeDetailsModal component
 */
export const VolumeDetailsModal: React.FC<VolumeDetailsModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  volumeId,
}) => {
  const { data: volume, isLoading, error } = useVolumeDetails(tenantId, volumeId);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>Volume Details</Modal.Header>
      <Modal.Body>
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        )}

        {/* Error State */}
        {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

        {/* Volume Details */}
        {volume && (
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Basic Information
              </h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Volume Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{volume.volumeName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Volume Code</dt>
                  <dd className="mt-1">
                    <CopyableCode value={volume.volumeCode} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Volume ID</dt>
                  <dd className="mt-1">
                    <CopyableCode value={volume.volumeId} />
                  </dd>
                </div>
                {volume.description && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                    <dd className="mt-1 text-sm text-gray-900">{volume.description}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Volume Configuration */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Configuration</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Max File Size</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {volume.maxFileSize ? `${volume.maxFileSize.toFixed(2)} MB` : 'Unlimited'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Current Usage</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {volume.totalSizeBytes ? formatBytes(volume.totalSizeBytes) : '0 Bytes'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Object Count</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {volume.objectCount || 0}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Volume Prefix</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">
                    {volume.volumePrefix}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Allowed File Extensions</dt>
                  <dd className="mt-1 flex flex-wrap gap-2">
                    {volume.allowedFileExtensions && volume.allowedFileExtensions.length > 0 ? (
                      volume.allowedFileExtensions.map((ext, index) => (
                        <Badge key={index} variant="info">
                          {ext}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-gray-500">All file extensions allowed</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Metadata */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Metadata</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(volume.createdAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Updated At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(volume.updatedAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
